/**
 * DJI flight data as the Hub reports it (ADN-2307). Dates arrive as ISO
 * strings. Storage keys never reach the client.
 */

/**
 * ready       the detailed flight file has been received
 * processing  the flight summary has arrived and the detail is still expected
 * unavailable no detail arrived in the expected window
 */
export type DjiDetailStatus = 'ready' | 'processing' | 'unavailable';

export type DjiUsageType = 'spray' | 'sow' | 'cargo_delivery';

export interface DjiFlight {
  intakeId: string;
  /** The ADN drone this flight was matched to; null if it has no drone link. */
  droneId: string | null;
  droneSource: 'adn' | 'organization' | null;
  flightStartedAt: string | null;
  flightEndedAt: string | null;
  /** The start as wall-clock time where the work was ('YYYY-MM-DDTHH:mm:ss'), like the times typed on the record. */
  flightStartedAtLocal: string | null;
  durationSeconds: number | null;
  workAreaAcres: number | null;
  usageType: DjiUsageType | null;
  sprayVolumeMl: number | null;
  sowVolumeG: number | null;
  /** Null until the detailed file is received. */
  avgSpeedMps: number | null;
  detailStatus: DjiDetailStatus;
}

/**
 * What a draft spray record started from a DJI flight would contain, exactly as the Hub would create it.
 * Times are wall-clock ('YYYY-MM-DDTHH:mm:ss') where the work was.
 */
export interface DjiDraftPreview {
  applicationDate: string;
  droneId: string;
  droneSource: 'adn' | 'organization';
  startTime: string;
  endTime: string;
  acreageSprayed: number;
  numFlights: number;
  /** Null until DJI's detailed file has reported a speed. */
  flightSpeedMph: number | null;
  detailStatus: DjiDetailStatus;
  /** The person the draft would be created for. */
  pilotName: string;
}

/** One DJI flight received for a drone. */
export interface DjiDroneFlight {
  intakeId: string;
  flightStartedAt: string | null;
  /** The start as wall-clock time where the work was ('YYYY-MM-DDTHH:mm:ss'); null if that time zone is unknown. */
  flightStartedAtLocal: string | null;
  durationSeconds: number | null;
  workAreaAcres: number | null;
  /** 'dismissed' when the pilot set the flight aside (not a real job). */
  linkStatus: string;
  /** Null when the flight has been received but is not linked to a spray record yet. */
  sprayRecordId: string | null;
  /** That record's status ('draft', 'pending', 'completed', 'cancelled'); null with no record. */
  sprayRecordStatus: string | null;
}

export interface DjiDroneStatus {
  state: 'receiving' | 'no_data';
  flightCount: number;
  /** How many of those flights are linked to a spray record. */
  linkedCount: number;
  /** How many the pilot dismissed; they are not waiting to be linked. */
  dismissedCount: number;
  lastFlightAt: string | null;
  /** Most recent first; may be fewer than flightCount. */
  recentFlights: DjiDroneFlight[];
}
